import React, { useCallback, useState } from 'react';
import { INTERNAL_ROLE_FILTER_OPTIONS, useGetInternalUsers } from '../../../domain/Users';
import useClampedPage from '../../../hooks/useClampedPage';
import { DebouncedSearchField } from '../../commonComponents/DebouncedSearchField';
import { DsPagination } from '../../commonComponents/DsPagination';
import { ListPage } from '../../commonComponents/ListPage';
import MultiSelect from '../../commonComponents/MultiSelect';
import { GotuAdminsTable } from './components/GotuAdminsTable';

/**
 * GoTu Admin (GOTU-5861) — the read-only list of internal staff and their
 * roles, sourced from auth-service's `internal_user` table.
 *
 * Search, role filter, sort and paging are all server-side; this page only
 * holds the three filter values and hands them to the query. The endpoint's
 * page size and the story's both being 15 is why nothing here configures it.
 *
 * Known gap, deliberate: `internal_user` has no status column and nothing
 * removes a row when someone leaves Google Workspace, so this lists everyone
 * who has ever signed in — departed staff included. Filtering to active admins
 * needs a Workspace sync that does not exist yet; see the ticket.
 */
export const GotuAdmins = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState([]);

  const {
    admins, totalCount, pageSize, isPending, isFetching, isError,
  } = useGetInternalUsers({ page, search, roles });

  // A page can outlive its data — narrowing a filter leaves an out-of-range
  // page requesting an empty slice. Pull it back into range.
  useClampedPage(page, totalCount, pageSize, setPage);

  // Re-slicing the list makes the current page number meaningless: page 4 of
  // the unfiltered list is not page 4 of the filtered one. Both filters reset
  // to the first page for that reason.
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleRolesChange = useCallback((value) => {
    setRoles(value);
    setPage(1);
  }, []);

  const hasFilters = search !== '' || roles.length > 0;

  return (
    <ListPage
      title="GoTu Admin"
      isLoading={isFetching && admins.length > 0}
      total={totalCount ?? undefined}
      filters={(
        <>
          <DebouncedSearchField
            label="Search by name"
            value={search}
            onChange={handleSearchChange}
          />

          {/* The design system has no multi-select; this is the shared one in
              its DS-styled `label` variant. An empty selection means "every
              role" — the client omits the param rather than sending role=. */}
          <MultiSelect
            label="Role"
            options={INTERNAL_ROLE_FILTER_OPTIONS}
            value={roles}
            onChange={handleRolesChange}
            placeholder="All"
            showAllButton
          />
        </>
      )}
    >
      <GotuAdminsTable
        admins={admins}
        isPending={isPending}
        isError={isError}
        hasFilters={hasFilters}
        footer={(
          <DsPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount ?? 0}
            onPageChange={setPage}
          />
        )}
      />
    </ListPage>
  );
};

export default GotuAdmins;
